/**
 * **사장님 계정 화면(로그인·매장 고르기·비밀번호 찾기·회원가입) 문구와 거절 → 한 줄 한 곳.** 의존성 없는 순수 모듈이다(Vue·Element·`@/` 별칭 금지).
 *
 * 정본: pos2-lite src/domain/owner/owner-copy.ts
 *
 * 같은 사장님 계정으로 POS2 Lite·오더엔 멀티·관리 웹 세 곳에 로그인하므로 **같은 문장**을 쓴다. 이 파일은 관리 웹이 쓰는 몫만 옮긴 사본이다 —
 * 리포·언어가 달라 추출하지 않는다. 정본의 문장을 고치면 이 사본도 같이 고친다(i18n 키가 한국어 문장 자체라 문장이 곧 번역 키다).
 * 관리 웹에만 있는 말(아이디 탭·왼쪽 띠·가입 안내 줄)은 아래 [ADMIN_LOGIN_COPY] 에, 콜팝 웹 가입에만 있는 말(매장 전화번호·관리점 코드 칸과
 * 그 거절)은 [CALLPOP_JOIN_COPY] 에 따로 둔다 — 정본에 없는 문장이다.
 */
// 확장자를 적는 이유: 단위 테스트가 앱 번들 밖(node)에서 읽는다 — node ESM 은 상대 경로 확장자를 추론하지 않는다.
import { httpErrorBody, httpStatusOf, isTransportError } from '../plugins/http/policy.js'
import { loginFailureText } from './loginFailure.js'
import {
  LOGIN_TAB,
  OWNER_INPUT_PROBLEM,
  OWNER_PASSWORD_PROBLEM,
  isLoginGateRejection,
  ownerRejectionOf,
} from './ownerLogin.js'
import { STORE_DRAFT_PROBLEM } from './ownerSignup.js'
import { TEL_KIND, telKindOf, telText } from './telNumber.js'

/** 정본 `OWNER_COPY` 가운데 관리 웹이 쓰는 줄 — 문장은 정본 그대로다 */
export const OWNER_COPY = Object.freeze({
  loginHeading: '사장님 계정으로 로그인해 주세요',
  tabPhone: '휴대폰 번호',
  tabEmail: '이메일',
  phonePlaceholder: '010-0000-0000',
  emailPlaceholder: 'example@email.com',
  password: '비밀번호',
  login: '로그인',
  forgotPassword: '비밀번호를 잊었어요',
  legacyEntry: '예전 오더엔 아이디(S26…)로 쓰셨나요?',
  backToLogin: '← 로그인으로',

  identityButton: '휴대폰 본인확인',
  identityPageTitle: '휴대폰 본인확인',
  /** 본인확인 단추 밑 한 줄 — 동의가 아니라 알림이다(정본 `OwnerIdentityNote`, 10-24 사용자 결정) */
  identityNote: '본인확인 결과(이름·휴대폰·연계정보)는 계정을 만들고 지키는 데만 써요',
  privacyPolicy: '개인정보 처리방침',

  pickKick: '매장 선택',
  pickTitle: '어느 매장으로 들어갈까요?',
  pickSub: (account, count) => `${account} 계정에 매장 ${count}개가 연결돼 있어요.`,
  pickEnter: '들어가기 →',
  pickStoreId: (storeId) => `매장 번호 ${storeId}`,
  pickOtherAccount: '다른 계정으로 로그인',

  reset1Kick: '비밀번호 재설정 1/2',
  reset1Title: '가입한 휴대폰 번호로 확인할게요',
  reset1Sub: '휴대폰 본인확인을 마치면 바로 새 비밀번호를 정할 수 있어요.',
  reset1Hint: '휴대폰 번호가 바뀌었어도 본인확인으로 계정을 찾아요.',
  reset2Kick: '비밀번호 재설정 2/2',
  reset2Title: '새 비밀번호를 정해 주세요',
  reset2Sub: (account) => `${account} 계정이에요.`,
  newPassword: '새 비밀번호',
  newPasswordConfirm: '새 비밀번호 확인',
  newPasswordPlaceholder: '영문·숫자 포함 8자 이상',
  passwordConfirmPlaceholder: '한 번 더 입력',
  /**
   * 바꾸기만 한다 — 관리 웹은 바꾼 뒤 로그인 칸으로 돌아가 보안 인증과 함께 다시 로그인한다. 그래서 정본의 `reset2Submit`(「비밀번호 바꾸고
   * 로그인」 — 앱이 곧바로 로그인하는 자리)이 아니라, 같은 말을 쓰는 정본 열쇠 `reset2SubmitInSession` 을 쓴다.
   */
  reset2SubmitInSession: '비밀번호 바꾸기',
  passwordChanged: '비밀번호를 바꿨어요',

  // ── 회원가입 3단계(콜팝 웹 가입 `views/CallpopJoinView` 가 쓴다) ──
  identityDone: '✓ 본인 확인이 끝났어요',
  previous: '← 이전',
  signup1Kick: '회원가입 1/3 · 본인 확인',
  signup1Title: '휴대폰 번호로 시작해요',
  signup1Sub: '이 번호가 로그인 아이디가 돼요. 비밀번호를 잊었을 때도 이 번호로 본인확인해요.',
  emailLabel: '이메일',
  emailHint: '선택 · 이메일로도 로그인하고 비밀번호를 찾을 수 있어요',
  signup1Next: '매장 정보 입력하기 →',
  haveAccount: '이미 계정이 있어요 · 로그인',

  signup2Kick: '회원가입 2/3 · 매장 정보',
  signup2Title: '어느 매장에서 사용하시나요?',
  signup2Sub: '적어 주신 정보로 매장 번호를 발급해요.',
  bizNo: '사업자등록번호',
  bizNoPlaceholder: '000-00-00000',
  storeNm: '매장명',
  storeNmPlaceholder: '예: 팔도씨피자 광명본점',
  storeNmHint: '간판이나 배달앱에 쓰는 매장 이름을 적어 주세요.',
  ceoNm: '대표자명',
  ceoNmPlaceholder: '예: 홍길동',
  businessType: '업종',
  businessTypes: Object.freeze({ RESTAURANT: '일반음식점', CAFE_DESSERT: '카페·디저트', OTHER: '기타' }),
  address: '매장 주소',
  addressPlaceholder: '도로명·건물명·지번으로 찾기 (예: 오리로 000)',
  addressHint: '배달앱에 등록한 주소와 같게 적으면 나중에 매장 연결이 쉬워요.',
  addressSearch: '주소 찾기',
  addressChange: '다시 찾기',
  addressNone: '찾는 주소가 없어요. 도로명이나 건물 이름으로 다시 찾아 주세요.',
  addressMore: '더 보기',
  addressDetailPlaceholder: '상세 주소 (예: 1층 101호) · 선택',
  modes: '어떤 방식으로 운영하시나요?',
  modesTag: '필수 · 여러 개',
  operationModes: Object.freeze({ DELIVERY: '배달', PACKING: '포장', HALL: '홀' }),
  cardPayment: '매장에서 카드결제를 받으시나요?',
  cardYes: '네, 받아요',
  cardNo: '아니요',
  modesInfoOnly:
    '운영 방식과 카드결제 여부는 매장 정보로만 저장돼요. 이것만으로 홀·포장 기능이나 카드결제가 켜지지는 않아요.',
  otherProgram: '다른 배달 주문 프로그램(타사 솔루션)도 쓰고 계신가요?',
  otherProgramHint: (appShortName) =>
    `설치·지원 때 참고하려고 여쭤봐요. 어떤 답이든 처음에는 ${appShortName} 프린터를 켜지 않아요.`,
  otherPrograms: (appName) =>
    Object.freeze({ USING: '네, 같이 써요', NONE: `아니요, ${appName}만 써요`, UNKNOWN: '잘 모르겠어요' }),
  required: '필수',
  optional: '선택',
  signup2Next: '다음 · 비밀번호 정하기 →',

  signup3Kick: '회원가입 3/3 · 비밀번호와 약관',
  signup3Title: '비밀번호를 정하고 마무리해요',
  signup3Sub: (hasEmail) => `로그인할 때 휴대폰 번호${hasEmail ? ' 또는 이메일' : ''}과 이 비밀번호를 써요.`,
  passwordConfirm: '비밀번호 확인',
  agreeAll: '전체 동의',
  signupSubmit: '동의하고 가입 완료',

  /** 이미 등록된 사업자로 막힌 가입에 서버가 실은 예전 아이디 줄(정본 `OwnerLegacyMatches`) */
  legacyMatches: '찾은 예전 아이디',
  legacyMatchLine: (accountId, storeNm) => `${accountId} · ${storeNm}`,
  legacyMatchLogin: '이 아이디로 로그인',
  legacyMatchLinked: (loginIdMasked) => `사장님 계정(${loginIdMasked})에 연결된 매장이에요`,
})

/**
 * 가입하는 앱의 이름 — 다른 프로그램 질문의 두 문장이 받는다. 콜팝 웹 가입은 POS2 Lite 를 받는다(W3·W9). 정본: pos2-lite
 * src/lib/owner/owner-product.ts `OWNER_APP_NAME` 의 `name`·`short`
 */
export const SIGNUP_APP_NAME = Object.freeze({ name: '오더엔 Lite', short: 'Lite' })

/** 사장님에게 문의하라고 알리는 곳 — 가입 거절이 쓴다(정본 `CUSTOMER_CENTER`) */
export const CUSTOMER_CENTER = '고객센터 1577-8828'

/**
 * 콜팝 웹 가입에만 있는 말 — 정본(Lite)에 없는 문장이다. 칸 둘(매장 전화번호 W8·관리점 코드 W4)과 서버의 두 거절, 그리고 관리 웹이 약관을
 * 상수 목록으로 대신하지 않아 생긴 「다시 불러오기」, 가입 뒤 세션을 못 열었을 때의 안내다. 사장님 화면의 말투를 따른다.
 */
export const CALLPOP_JOIN_COPY = Object.freeze({
  pageTitle: '콜팝 가맹점 가입',
  /** 단계 점 밑의 이름 — 시안(v5.3)의 세 단계 */
  stepLabels: Object.freeze(['본인 확인', '매장 정보', '비밀번호·약관']),
  intro: '휴대폰 본인확인 → 매장 정보 → 비밀번호·약관 순서로 적어요. 가입하면 바로 서류 3종을 올리는 화면으로 가요.',
  tel: '매장 전화번호',
  telPlaceholder: '예: 02-123-4567',
  agencyCode: '관리점 코드',
  agencyCodePlaceholder: '예: BR0001',
  agencyCodeHint: '관리점에서 받은 코드가 있으면 적어 주세요. 없으면 비워 두세요.',
  /**
   * 관리점 칸 밑에 늘 보이는 한 줄(W20) — 가입 때 매핑되는 순간 운영사·관리점이 기존 대행 권한으로 매장 정보를 본다. 체크칸 없이 가입을 마치는
   * 것이 동의다. 처리방침 5절 표와 같은 항목이라 **글자를 고정**한다(계획 §2 W20).
   */
  orgViewNotice:
    '콜팝 운영사·관리점이 매장 운영을 돕기 위해 이 매장의 정보(매장 이름·대표자 이름·매장 전화번호·주소·사업자 이메일·예금주)를 봅니다. 가입하면 이 내용에 동의하는 것으로 봅니다.',
  telRequired: '매장 전화번호를 적어 주세요.',
  telInvalid: '매장 전화번호를 다시 확인해 주세요. 지역번호부터 적어 주세요.',
  agencyCodeInvalid: '관리점 코드를 다시 확인해 주세요. 모르시면 비워 두세요.',
  termsFailed: '약관을 불러오지 못했어요. 다시 불러와 주세요.',
  termsReload: '다시 불러오기',
  /** 가입은 됐는데 그 표로 세션을 열지 못했다 — 계정은 이미 있다(다시 가입하면 이미 가입된 번호다) */
  enterFailed: '가입은 끝났어요. 로그인한 뒤 서류를 올려 주세요.',
  goLogin: '로그인하러 가기',
})

/** 관리 웹에만 있는 말 — 정본(Lite)에 없는 문장이다. 사장님 화면의 말투를 따른다 */
export const ADMIN_LOGIN_COPY = Object.freeze({
  /** 왼쪽 띠 — 로고 밑 한 줄 */
  brandLine: '매장·메뉴·주문·매출을 한곳에서 관리해요',
  tabId: '아이디',
  idHeading: '아이디로 로그인해 주세요',
  idSub: '관리자·관리점 계정이나 예전 매장 아이디(S…)로 들어가요.',
  /** 휴대폰·이메일 탭 맨 아래 — 셀프 가입은 아직 앱(Test 판)에만 있어 링크가 없다 */
  joinLine: '처음이신가요? 가입은 POS2 Lite·오더엔 멀티 앱에서 해요.',
  /** 휴대폰·이메일 탭에 아이디를 쳤다(`ownerLogin.isAccountIdLike`) — 정본은 예전 S… 만 「예전 아이디」 단추로 보내지만 관리 웹은 [아이디] 탭이 입구다 */
  idInOwnerTab: '관리자·관리점 아이디나 예전 매장 아이디(S…)는 [아이디] 탭에서 들어가요.',
})

/**
 * 개발사 표시 — 로그인 화면 왼쪽 띠 맨 아래 두 줄. 정본: pos2-lite src/components/DeveloperAttribution.tsx
 * (`DEVELOPER_CONTACT_LABEL`·`POWERED_BY_LABEL`) — 같은 글자를 쓴다. 회사 이름 줄은 번역하지 않는다.
 */
export const DEVELOPER_ATTRIBUTION = Object.freeze({
  contact: '개발사 문의 1577-8828',
  poweredBy: 'Powered By 오투오페이먼트 O2OPAYMENT',
})

/** 로그인 탭의 이름 — 탭 차례는 `ownerLogin.LOGIN_TABS` */
export const LOGIN_TAB_LABEL = Object.freeze({
  [LOGIN_TAB.PHONE]: OWNER_COPY.tabPhone,
  [LOGIN_TAB.EMAIL]: OWNER_COPY.tabEmail,
  [LOGIN_TAB.ID]: ADMIN_LOGIN_COPY.tabId,
})

/** 로그인 입력 문제 → 한 줄(아이디 꼴만 관리 웹의 말 — [ADMIN_LOGIN_COPY.idInOwnerTab]) */
export function ownerLoginInputText(problem) {
  switch (problem) {
    case OWNER_INPUT_PROBLEM.PHONE:
      return '휴대폰 번호를 확인해 주세요. 010으로 시작하는 11자리예요.'
    case OWNER_INPUT_PROBLEM.EMAIL:
      return '이메일 형식을 확인해 주세요.'
    case OWNER_INPUT_PROBLEM.ID_TAB:
      return ADMIN_LOGIN_COPY.idInOwnerTab
    case OWNER_INPUT_PROBLEM.PASSWORD_EMPTY:
      return '비밀번호를 넣어 주세요.'
    default:
      return ''
  }
}

export const NETWORK_TEXT = '서버에 연결하지 못했어요. 인터넷 연결을 확인하고 다시 해 주세요.'
export const UNKNOWN_TEXT = '처리하지 못했어요. 잠시 뒤 다시 해 주세요.'
const TICKET_EXPIRED_TEXT = '확인한 지 오래돼 처음부터 다시 해 주세요.'

/** 시도 한도(`LOGIN_LOCKED`)가 풀리기까지 몇 분 — 1분보다 짧아도 1분이라 한다. 서버가 시간을 싣지 않았으면 `null` */
export function ownerRetryMinutes(rejection) {
  return rejection.retryAfterSec === null ? null : Math.max(1, Math.ceil(rejection.retryAfterSec / 60))
}

/** 로그인 거절 → 한 줄 */
export function ownerLoginRejectionText(rejection, tab) {
  switch (rejection.code) {
    case 'OWNER_NOT_FOUND':
      return (rejection.by || (tab === LOGIN_TAB.EMAIL ? 'EMAIL' : 'PHONE')) === 'EMAIL'
        ? '이 이메일로 가입된 계정이 없어요. 가입할 때 이메일을 적지 않았다면 휴대폰 번호로 로그인해 주세요.'
        : '가입된 번호가 아니에요. 회원가입을 하시거나 번호를 다시 확인해 주세요.'
    case 'WRONG_PASSWORD':
      return '비밀번호가 맞지 않아요. 잊으셨다면 [비밀번호를 잊었어요]로 본인확인 후 바로 바꿀 수 있어요.'
    case 'LOGIN_LOCKED': {
      const minutes = ownerRetryMinutes(rejection)
      return minutes === null
        ? '비밀번호를 여러 번 틀려 잠시 로그인할 수 없어요. 조금 뒤에 다시 해 주세요.'
        : `비밀번호를 여러 번 틀려 잠시 로그인할 수 없어요. ${minutes}분 뒤에 다시 해 주세요.`
    }
    default:
      return rejection.status >= 500 ? UNKNOWN_TEXT : '로그인하지 못했어요. 아이디와 비밀번호를 확인해 주세요.'
  }
}

/** 매장에 들어가기(`login/owner/store`) 거절·표 없음 → 한 줄 */
export function ownerEnterStoreRejectionText(rejection) {
  if (rejection === null || rejection.code === 'TICKET_INVALID') return '로그인한 지 오래돼 다시 로그인해 주세요.'
  if (rejection.code === 'STORE_NOT_LINKED') return '이 계정에 연결된 매장이 아니에요. 매장을 다시 골라 주세요.'
  return rejection.status >= 500 ? UNKNOWN_TEXT : '매장에 들어가지 못했어요. 다시 골라 주세요.'
}

export const IDENTITY_FAILED_TEXT = '휴대폰 본인확인을 마치지 못했어요. 다시 해 주세요.'

/** 비밀번호 재설정 — 확인한 휴대폰 번호를 다른 계정이 쓴다 */
const PHONE_IN_OTHER_ACCOUNT_TEXT = '이 번호는 다른 계정에서 쓰고 있어요. 가입할 때 적은 이메일로 로그인해 주세요.'

/** 이미 가입한 사람 — 서버가 실은 가린 아이디가 있으면 함께 알린다(가입 목적의 거절 — 정본과 같은 줄로 둔다) */
function ownerExistsText(rejection) {
  const account = rejection.loginIdMasked === null ? '' : `(${rejection.loginIdMasked})`
  return `이미 가입된 번호예요${account}. 로그인해 주세요.`
}

/** 본인확인 표 받기(`POST /api/owner/identity`) 거절 → 한 줄 */
export function ownerIdentityRejectionText(rejection) {
  switch (rejection.code) {
    case 'OWNER_EXISTS':
      return ownerExistsText(rejection)
    case 'OWNER_NOT_FOUND':
      return '이 번호로 가입된 계정이 없어요.'
    case 'PHONE_IN_USE':
      return PHONE_IN_OTHER_ACCOUNT_TEXT
    case 'NICE_RESULT_INVALID':
      return IDENTITY_FAILED_TEXT
    default:
      return rejection.status >= 500 ? UNKNOWN_TEXT : IDENTITY_FAILED_TEXT
  }
}

/** 비밀번호 재설정(`password/reset`) 거절 → 한 줄 */
export function ownerPasswordResetRejectionText(rejection) {
  if (rejection.code === 'PASSWORD_POLICY') return ownerPasswordText(OWNER_PASSWORD_PROBLEM.POLICY)
  if (rejection.code === 'PHONE_IN_USE') return PHONE_IN_OTHER_ACCOUNT_TEXT
  if (rejection.code === 'TICKET_INVALID') return TICKET_EXPIRED_TEXT
  return rejection.status >= 500 ? UNKNOWN_TEXT : '비밀번호를 바꾸지 못했어요. 다시 해 주세요.'
}

/** 새 비밀번호 문제 → 한 줄 */
export function ownerPasswordText(problem) {
  switch (problem) {
    case OWNER_PASSWORD_PROBLEM.POLICY:
      return '비밀번호는 영문과 숫자를 넣어 8자 이상으로 정해 주세요.'
    case OWNER_PASSWORD_PROBLEM.SAME_AS_ID:
      return '아이디와 다른 비밀번호를 정해 주세요.'
    case OWNER_PASSWORD_PROBLEM.MISMATCH:
      return '비밀번호 확인이 달라요. 한 번 더 입력해 주세요.'
    default:
      return ''
  }
}

// ─── 회원가입(정본 owner-copy.ts 의 가입 몫 + 콜팝 웹 가입의 칸 둘) ───────────────────────────────────────

/** 필수 약관을 다 고르지 않았다 */
export const CONSENT_TEXT = '필수 약관에 모두 동의해 주세요.'

/** 가입 ①의 이메일(선택) 꼴이 틀렸다 */
export const OPTIONAL_EMAIL_TEXT = '이메일 형식을 확인해 주세요. 쓰지 않으시면 비워 두세요.'

/** 약관 줄(정본 `TERMS_COPY` 가운데 가입 ③이 쓰는 줄) */
export const TERMS_COPY = Object.freeze({
  view: '보기',
  hide: '접기',
  /** 보낸 판·문서가 지금 판과 어긋났다(409 `TERMS_OUTDATED`·400 `CONSENT_REQUIRED`) — 다시 읽고 다시 묻는다 */
  outdated: '약관이 바뀌었어요. 바뀐 약관을 확인하고 다시 동의해 주세요.',
})

/** 매장 정보 문제 → 한 줄(매장 전화번호만 콜팝 웹 가입의 말 — [CALLPOP_JOIN_COPY.telRequired]) */
export function ownerStoreDraftText(problem) {
  switch (problem) {
    case STORE_DRAFT_PROBLEM.BIZ_NUM:
      return '사업자등록번호를 다시 확인해 주세요.'
    case STORE_DRAFT_PROBLEM.STORE_NM:
      return '매장명을 적어 주세요.'
    case STORE_DRAFT_PROBLEM.CEO_NM:
      return '대표자명을 적어 주세요.'
    case STORE_DRAFT_PROBLEM.TEL:
      return CALLPOP_JOIN_COPY.telRequired
    case STORE_DRAFT_PROBLEM.TEL_INVALID:
      return CALLPOP_JOIN_COPY.telInvalid
    case STORE_DRAFT_PROBLEM.ADDRESS:
      return '매장 주소를 찾아 골라 주세요.'
    case STORE_DRAFT_PROBLEM.ADDRESS_DETAIL:
      return '상세 주소는 50자 안으로 적어 주세요.'
    case STORE_DRAFT_PROBLEM.MODES:
      return '운영 방식을 하나 이상 골라 주세요.'
    case STORE_DRAFT_PROBLEM.CARD:
      return '매장 카드결제 여부를 골라 주세요.'
    default:
      return ''
  }
}

/**
 * **가입 거절 → 한 줄과 돌아갈 걸음**(정본 `ownerSignupRejection` — 본인확인부터 다시·이메일 고치기·매장 정보 고치기·비밀번호 고치기·약관
 * 다시 읽기). 이미 등록된 사업자(`BIZ_NUM_EXISTS`)인데 서버가 그 번호의 예전 아이디를 실었으면 그 줄들(`legacyStores`)을 함께 돌려준다.
 * 콜팝 웹 가입의 거절 둘(`AGENCY_CODE_INVALID`·`TEL_REQUIRED`, 계획 §11.2)은 매장 정보로 돌아간다. 걸음 → 화면의 단계는
 * `ownerSignup.signupStepOf`.
 *
 * @param {object} rejection `ownerLogin.ownerRejectionOf` 의 답
 * @returns {{text: string, step: ?string, field?: string, legacyStores?: Array<object>}}
 */
export function ownerSignupRejection(rejection) {
  switch (rejection.code) {
    case 'TICKET_INVALID':
      return { text: TICKET_EXPIRED_TEXT, step: 'IDENTITY' }
    case 'OWNER_EXISTS':
      return { text: ownerExistsText(rejection), step: 'IDENTITY' }
    case 'PHONE_IN_USE':
      return { text: `이미 다른 계정에서 쓰는 휴대폰 번호예요. ${CUSTOMER_CENTER}로 문의해 주세요.`, step: 'IDENTITY' }
    case 'EMAIL_IN_USE':
      return { text: '이미 다른 계정에서 쓰는 이메일이에요.', step: 'EMAIL' }
    case 'BIZ_NUM_INVALID':
      return { text: ownerStoreDraftText(STORE_DRAFT_PROBLEM.BIZ_NUM), step: 'STORE' }
    // 국세청 사업자 상태 — 서버가 가입 때 조회한다. 조회하지 못했으면(503) 그 자리에 머문다 — 사장님이 다시 누를 때만 다시 보낸다
    case 'BIZ_NUM_CLOSED':
      return { text: '국세청에 폐업으로 나오는 사업자등록번호예요. 번호를 확인해 주세요.', step: 'STORE' }
    case 'BIZ_NUM_UNREGISTERED':
      return { text: '국세청에 등록되지 않은 사업자등록번호예요. 번호를 다시 확인해 주세요.', step: 'STORE' }
    case 'BIZ_NUM_CHECK_UNAVAILABLE':
      return { text: '지금은 사업자 상태를 확인할 수 없어요. 잠시 뒤 다시 해 주세요.', step: null }
    case 'BIZ_NUM_EXISTS':
      // 사업자등록번호는 누구나 알 수 있어, 서버는 본인확인 이름이 그 매장 대표자와 같을 때만 예전 아이디를 싣는다
      if (rejection.legacyStores.length > 0) {
        return {
          text: '이미 등록된 사업자예요. 예전 오더엔 아이디로 로그인해 주세요.',
          step: 'STORE',
          legacyStores: rejection.legacyStores,
        }
      }
      return {
        text:
          rejection.ceoMatches === true
            ? `이미 등록된 사업자예요. ${CUSTOMER_CENTER}에서 이 계정에 연결해 드려요.`
            : `다른 분이 등록한 사업자등록번호예요. ${CUSTOMER_CENTER}로 문의해 주세요.`,
        step: 'STORE',
      }
    case 'ADDRESS_INVALID':
      return { text: '매장 주소를 다시 골라 주세요.', step: 'STORE' }
    // 콜팝 웹 가입에만 있는 거절 둘(계획 §11.2) — 둘 다 매장 정보의 한 칸이라 그 칸(`field`) 밑에서 말한다
    case 'AGENCY_CODE_INVALID':
      return { text: CALLPOP_JOIN_COPY.agencyCodeInvalid, step: 'STORE', field: 'agencyCode' }
    case 'TEL_REQUIRED':
      return { text: CALLPOP_JOIN_COPY.telRequired, step: 'STORE', field: 'tel' }
    case 'PASSWORD_POLICY':
      return { text: ownerPasswordText(OWNER_PASSWORD_PROBLEM.POLICY), step: 'PASSWORD' }
    // 화면이 필수를 먼저 검사하므로 필수 빠짐(400 `CONSENT_REQUIRED`)은 화면의 목록이 서버의 지금 판과 어긋났다는 뜻이다 — 낡은 판(409)과 같이
    // 약관을 다시 읽는다
    case 'CONSENT_REQUIRED':
    case 'TERMS_OUTDATED':
      return { text: TERMS_COPY.outdated, step: 'TERMS' }
    case 'NOT_CONFIGURED':
      return { text: '지금은 가입을 받을 수 없어요. 잠시 뒤 다시 해 주세요.', step: null }
    case 'INVALID_REQUEST':
      return { text: '입력한 내용을 다시 확인해 주세요.', step: 'STORE' }
    default:
      return {
        text: rejection.status >= 500 ? UNKNOWN_TEXT : '가입하지 못했어요. 입력한 내용을 확인해 주세요.',
        step: null,
      }
  }
}

/**
 * **가입 요청이 실패했을 때 — 한 줄과 돌아갈 걸음.** 전송 오류(응답 없음)는 연결 문장·그 자리, HTTP 가 아닌 실패는 「처리하지 못했어요」·그 자리,
 * 가입이 닫혀 있으면(503 `NOT_CONFIGURED` + 서버 문장) 그 문장·그 자리,
 * 그 밖은 서버 거절 봉투로 [ownerSignupRejection](정본은 Rust 경계가 앞의 둘을 `ownerInvokeFailureText` 로 접는다 — 관리 웹은 axios 실패 모양을
 * 이 한 곳에서 읽는다).
 *
 * @param {*} error 요청이 던진 것
 * @returns {{text: string, step: ?string, legacyStores?: Array<object>}}
 */
export function ownerSignupFailureOf(error) {
  if (isTransportError(error)) return { text: NETWORK_TEXT, step: null }

  const status = httpStatusOf(error)
  if (status == null) return { text: UNKNOWN_TEXT, step: null }

  const body = httpErrorBody(error)
  // 콜팝 웹 가입이 닫혀 있다(서류 저장 설정 없음 — G1, 503 NOT_CONFIGURED) — 서버 문장을 그대로, 단계는 그 자리(관리 웹에만 있는 갈래 —
  // 정본의 같은 코드 문장은 앱 셀프 가입의 것이다)
  if (body && body.code === 'NOT_CONFIGURED' && typeof body.message === 'string' && body.message.trim()) {
    return { text: body.message.trim(), step: null }
  }
  return ownerSignupRejection(ownerRejectionOf(status, body))
}

/** 주소를 찾지 못했다(요청 실패·형식 밖) — 정본 pos2-lite src/lib/owner/owner-flows.ts `addressPageOf` 의 문장 */
export const ADDRESS_FAILED_TEXT = '주소를 찾지 못했어요. 잠시 뒤 다시 찾아 주세요.'

/** 표시용 휴대폰 번호(010-0000-0000) — 이메일·가린 값은 그대로 */
export function ownerAccountLabel(loginId) {
  return telKindOf(loginId) === TEL_KIND.MOBILE ? telText(loginId) : String(loginId == null ? '' : loginId)
}

/**
 * **사장님 계정 요청이 실패했을 때 폼이 말할 한 줄** — 어느 입구든 같은 순서다(관리 웹의 요청 실패 모양을 정본의 거절 읽기에 맞춘다).
 *
 * 1. 전송 오류(응답 없음) → 연결 문장
 * 2. 관리 웹 로그인 관문의 거절(`401 {error, code}` — 보안 인증·조직 비활성) → 아이디 로그인과 같은 문장(`loginFailureText`, 서버 문장 그대로)
 * 3. 그 밖의 HTTP 실패 → 그 입구의 거절 문장([rejectionText] — 코드가 없는 5xx 는 정본처럼 「처리하지 못했어요」)
 * 4. HTTP 가 아닌 실패(스크립트 오류) → 「처리하지 못했어요」(정본 `ownerInvokeFailureText`)
 *
 * @param {*} error 요청이 던진 것
 * @param {function(object): string} rejectionText 그 입구의 거절 → 한 줄(위의 `owner…RejectionText`)
 * @returns {string}
 */
export function ownerFailureText(error, rejectionText) {
  if (isTransportError(error)) return NETWORK_TEXT

  const status = httpStatusOf(error)
  if (status == null) return UNKNOWN_TEXT

  const body = httpErrorBody(error)
  if (isLoginGateRejection(body)) return loginFailureText(error)

  return rejectionText(ownerRejectionOf(status, body))
}
